import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Inbox, Users as UsersIcon } from "lucide-react";
import { PageHeader, Badge, EmptyState } from "@/components/Common";
import { useAuth } from "@/contexts/AuthContext";

export default function Messages() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ to_user: "broadcast", subject: "", body: "" });

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: m }, { data: u }] = await Promise.all([api.get("/messages"), api.get("/users")]);
        if (!cancelled) { setItems(m); setUsers(u); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  const load = () => setRefreshKey((k) => k + 1);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/messages", {
        to_user: form.to_user === "broadcast" ? null : form.to_user,
        subject: form.subject,
        body: form.body,
      });
      toast.success("Message sent");
      setForm({ to_user: "broadcast", subject: "", body: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const inbox = items.filter((m) => !m.to_user || m.to_user === user?.id);
  const sent = items.filter((m) => m.from_user === user?.id);

  return (
    <div>
      <PageHeader
        eyebrow="Communications"
        title="Internal Messaging"
        subtitle="Send direct messages to colleagues or broadcast company-wide notices."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-gray-200 rounded-md p-5 sticky top-20">
            <h3 className="font-display font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Send size={15} className="text-emerald-800" /> Compose
            </h3>
            <form onSubmit={send} className="space-y-3" data-testid="message-form">
              <div>
                <Label>Recipient</Label>
                <Select value={form.to_user} onValueChange={(v) => setForm({ ...form, to_user: v })}>
                  <SelectTrigger className="mt-1" data-testid="message-to-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="broadcast">📢 Broadcast (everyone)</SelectItem>
                    {users.filter((u) => u.id !== user?.id).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name} · {u.role?.replaceAll("_", " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="mt-1" data-testid="message-subject-input" />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea rows={5} required value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  className="mt-1" data-testid="message-body-input" />
              </div>
              <Button type="submit" disabled={busy}
                className="w-full bg-emerald-900 hover:bg-emerald-950 text-white"
                data-testid="message-send-button">
                {busy ? "Sending…" : "Send"}
              </Button>
            </form>
          </div>
        </div>

        {/* Inbox + Sent */}
        <div className="lg:col-span-2 space-y-6">
          <Section title="Inbox" icon={Inbox} items={inbox} loading={loading} self={user} />
          <Section title="Sent" icon={Send} items={sent} loading={loading} self={user} />
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, items, loading, self }) {
  return (
    <div className="bg-white border border-gray-200 rounded-md">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
        <Icon size={15} className="text-emerald-800" />
        <h3 className="font-display font-semibold text-gray-900">{title}</h3>
        <span className="text-xs text-gray-500">({items.length})</span>
      </div>
      {loading ? (
        <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState title="No messages here yet" hint="Compose a new message on the left." />
      ) : (
        <ul className="divide-y divide-gray-100" data-testid={`messages-${title.toLowerCase()}`}>
          {items.map((m) => (
            <li key={m.id} className="px-5 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {!m.to_user && <Badge tone="amber">broadcast</Badge>}
                    <span className="font-medium text-gray-900 truncate">
                      {m.from_user === self?.id ? `To: ${m.to_user_name || "Everyone"}` : `From: ${m.from_user_name}`}
                    </span>
                  </div>
                  {m.subject && <div className="text-sm font-medium text-gray-800 mt-0.5">{m.subject}</div>}
                  <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{m.body}</div>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 flex-shrink-0">
                  {new Date(m.created_at).toLocaleString()}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
